import React from 'react';
import { ClientDataSource, ServerDataSource } from '@bingads-webui-react/data-source';
import { EventProvider } from '@bingads-webui-react/event-provider';
import { DataSelection } from '@bingads-webui-react/data-selection';
import { DataAggregation } from './context';
import { DrawerContainer, InlineContainer, EmbeddedContainer } from './container';
import { SelectionTags, EntitySelectorSearchBoxProps, Tree } from './components';
import {
  FetchDataResponseType, I18n, ItemType, SelectionFlatTreeProps,
} from './types';

const DefaultSearchFn = (items: any[], searchTerm: string) => items.filter((item) => item.content?.toLowerCase().includes(searchTerm.toLowerCase()));

export const ServerDataSourceWrapper = ({
  fetchData, children, value, onChange,
}) => (
  <ServerDataSource fetchData={fetchData}>
    <DataAggregation>
      <DataSelection value={value} onChange={onChange}>
        <EventProvider>
          {children}
        </EventProvider>
      </DataSelection>
    </DataAggregation>
  </ServerDataSource>
);

export const ClientDataSourceWrapper = ({
  data, searchFn, paginate, children, value, onChange,
}) => (
  <ClientDataSource data={data} paginate={paginate} searchFn={searchFn ?? DefaultSearchFn}>
    <DataAggregation>
      <DataSelection value={value} onChange={onChange}>
        <EventProvider>
          {children}
        </EventProvider>
      </DataSelection>
    </DataAggregation>
  </ClientDataSource>
);

interface EntitySelectorBaseProps {
  i18n: I18n;
  value: ItemType[];
  onChange: (value: any[]) => void;
  customFilter?: (item: any, search: string) => boolean;
}

interface EntitySelectorTreeBaseProps {
  selectionMode: SelectionFlatTreeProps['selectionMode'];
  customFilter?: (item: any, search: string) => boolean;
  onRenderItemContent?: SelectionFlatTreeProps['onRenderContent'];
}

interface EntitySelectorServerDataProps {
  fetchData: (params: any) => Promise<FetchDataResponseType>;
}

interface EntitySelectorClientDataProps {
  data: ItemType[];
  paginate?: boolean;
  searchFn?: (items: any[], searchTerm: string) => any[];
}

interface EntitySelectorTagsProps {
  tagViewLimit?: number;
  onRenderTagContent?: (item: any) => React.ReactNode;
}

interface EntitySelectorInlineContainerProps {
  className?: string;
  label?: string;
  searchboxProps?: EntitySelectorSearchBoxProps;
  popOverContentBefore?: React.ReactNode;
  popOverContentAfter?: React.ReactNode;
}

interface EntitySelectorDrawerContainerProps {
  titleText: string;
  confirmButtonText?: string;
  open: boolean;
  onConfirm?: (dataSelection: any[]) => void;
  onOpenChange: (open: boolean) => void;
  className?: string;
}

interface EntitySelectorEmbeddedContainerProps {
  className?: string;
  children?: any;
  childrenProps?: any;
}

type ServerEntitySelectorProps = EntitySelectorBaseProps & EntitySelectorTreeBaseProps & EntitySelectorServerDataProps & EntitySelectorTagsProps & EntitySelectorInlineContainerProps & EntitySelectorDrawerContainerProps;

export const ServerEntitySelector: React.FC<ServerEntitySelectorProps> = ({
  i18n,
  className,
  fetchData,
  customFilter,
  selectionMode,
  value,
  tagViewLimit,
  label,
  searchboxProps,
  popOverContentBefore,
  popOverContentAfter,
  open,
  titleText,
  confirmButtonText,
  onRenderTagContent,
  onChange,
  onConfirm,
  onOpenChange,
  onRenderItemContent,
}) => (
  <ServerDataSourceWrapper
    fetchData={fetchData}
    value={value}
    onChange={onChange}
  >
    <SelectionTags
      i18n={i18n}
      tagViewLimit={tagViewLimit}
      onRenderTagContent={onRenderTagContent}
    />
    <InlineContainer
      i18n={i18n}
      className={className}
      selectionMode={selectionMode}
      showBrowseButton
      label={label}
      searchboxProps={searchboxProps}
      customFilter={customFilter}
      onRenderItemContent={onRenderItemContent}
      popOverContentBefore={popOverContentBefore}
      popOverContentAfter={popOverContentAfter}
    >
      <Tree />
    </InlineContainer>
    <DrawerContainer
      i18n={i18n}
      selectionMode={selectionMode}
      titleText={titleText}
      confirmButtonText={confirmButtonText}
      customFilter={customFilter}
      open={open}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
      onRenderItemContent={onRenderItemContent}
    >
      <Tree />
    </DrawerContainer>
  </ServerDataSourceWrapper>
);

type ServerEntitySelectorInlineProps = EntitySelectorBaseProps & EntitySelectorTreeBaseProps & EntitySelectorServerDataProps & EntitySelectorTagsProps & EntitySelectorInlineContainerProps;

export const ServerEntitySelectorInline: React.FC<ServerEntitySelectorInlineProps> = ({
  i18n,
  className,
  fetchData,
  customFilter,
  selectionMode,
  value,
  tagViewLimit,
  label,
  searchboxProps,
  popOverContentBefore,
  popOverContentAfter,
  onRenderItemContent,
  onRenderTagContent,
  onChange,
}) => (
  <ServerDataSourceWrapper
    fetchData={fetchData}
    value={value}
    onChange={onChange}
  >
    <SelectionTags
      i18n={i18n}
      tagViewLimit={tagViewLimit}
      onRenderTagContent={onRenderTagContent}
    />
    <InlineContainer
      i18n={i18n}
      className={className}
      selectionMode={selectionMode}
      label={label}
      searchboxProps={searchboxProps}
      customFilter={customFilter}
      onRenderItemContent={onRenderItemContent}
      popOverContentBefore={popOverContentBefore}
      popOverContentAfter={popOverContentAfter}
    >
      <Tree />
    </InlineContainer>
  </ServerDataSourceWrapper>
);

type ServerEntitySelectorDrawerProps = EntitySelectorBaseProps & EntitySelectorTreeBaseProps & EntitySelectorServerDataProps & EntitySelectorDrawerContainerProps;

export const ServerEntitySelectorDrawer: React.FC<ServerEntitySelectorDrawerProps> = ({
  i18n,
  fetchData,
  customFilter,
  selectionMode,
  value,
  open,
  titleText,
  confirmButtonText,
  onChange,
  onConfirm,
  onOpenChange,
  onRenderItemContent,
}) => (
  <ServerDataSourceWrapper
    fetchData={fetchData}
    value={value}
    onChange={onChange}
  >
    <DrawerContainer
      i18n={i18n}
      selectionMode={selectionMode}
      titleText={titleText}
      confirmButtonText={confirmButtonText}
      customFilter={customFilter}
      open={open}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
      onRenderItemContent={onRenderItemContent}
    >
      <Tree />
    </DrawerContainer>
  </ServerDataSourceWrapper>
);

type ServerEntitySelectorEmbeddedProps = EntitySelectorBaseProps & EntitySelectorServerDataProps & EntitySelectorEmbeddedContainerProps;

export const ServerEntitySelectorEmbedded: React.FC<ServerEntitySelectorEmbeddedProps> = ({
  i18n,
  fetchData,
  className,
  value,
  onChange,
  children,
  customFilter,
  childrenProps,
}) => (
  <ServerDataSourceWrapper
    fetchData={fetchData}
    value={value}
    onChange={onChange}
  >
    <EmbeddedContainer
      i18n={i18n}
      className={className}
      customFilter={customFilter}
      childrenProps={childrenProps}
    >
      {children}
    </EmbeddedContainer>
  </ServerDataSourceWrapper>
);

type ClientEntitySelectorProps = EntitySelectorBaseProps & EntitySelectorTreeBaseProps & EntitySelectorClientDataProps & EntitySelectorTagsProps & EntitySelectorInlineContainerProps & EntitySelectorDrawerContainerProps;

export const ClientEntitySelector: React.FC<ClientEntitySelectorProps> = ({
  i18n,
  className,
  data,
  paginate,
  searchFn,
  selectionMode,
  customFilter,
  value,
  open,
  tagViewLimit,
  label,
  searchboxProps,
  popOverContentBefore,
  popOverContentAfter,
  titleText,
  confirmButtonText,
  onRenderTagContent,
  onChange,
  onConfirm,
  onOpenChange,
  onRenderItemContent,
}) => (
  <ClientDataSourceWrapper
    data={data}
    paginate={paginate}
    searchFn={searchFn}
    value={value}
    onChange={onChange}
  >
    <SelectionTags
      i18n={i18n}
      tagViewLimit={tagViewLimit}
      onRenderTagContent={onRenderTagContent}
    />
    <InlineContainer
      i18n={i18n}
      className={className}
      selectionMode={selectionMode}
      showBrowseButton
      label={label}
      searchboxProps={searchboxProps}
      customFilter={customFilter}
      onRenderItemContent={onRenderItemContent}
      popOverContentBefore={popOverContentBefore}
      popOverContentAfter={popOverContentAfter}
    >
      <Tree />
    </InlineContainer>
    <DrawerContainer
      i18n={i18n}
      selectionMode={selectionMode}
      titleText={titleText}
      confirmButtonText={confirmButtonText}
      open={open}
      customFilter={customFilter}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
      onRenderItemContent={onRenderItemContent}
    >
      <Tree />
    </DrawerContainer>
  </ClientDataSourceWrapper>
);

type ClientEntitySelectorInlineProps = EntitySelectorBaseProps & EntitySelectorTreeBaseProps & EntitySelectorClientDataProps & EntitySelectorTagsProps & EntitySelectorInlineContainerProps;

export const ClientEntitySelectorInline: React.FC<ClientEntitySelectorInlineProps> = ({
  i18n,
  className,
  data,
  paginate,
  searchFn,
  customFilter,
  selectionMode,
  value,
  tagViewLimit,
  label,
  searchboxProps,
  popOverContentBefore,
  popOverContentAfter,
  onRenderItemContent,
  onRenderTagContent,
  onChange,
}) => (
  <ClientDataSourceWrapper
    data={data}
    paginate={paginate}
    searchFn={searchFn}
    value={value}
    onChange={onChange}
  >
    <SelectionTags
      i18n={i18n}
      tagViewLimit={tagViewLimit}
      onRenderTagContent={onRenderTagContent}
    />
    <InlineContainer
      i18n={i18n}
      className={className}
      selectionMode={selectionMode}
      label={label}
      searchboxProps={searchboxProps}
      customFilter={customFilter}
      onRenderItemContent={onRenderItemContent}
      popOverContentBefore={popOverContentBefore}
      popOverContentAfter={popOverContentAfter}
    >
      <Tree />
    </InlineContainer>
  </ClientDataSourceWrapper>
);

type ClientEntitySelectorDrawerProps = EntitySelectorBaseProps & EntitySelectorTreeBaseProps & EntitySelectorClientDataProps & EntitySelectorDrawerContainerProps;

export const ClientEntitySelectorDrawer: React.FC<ClientEntitySelectorDrawerProps> = ({
  i18n,
  data,
  paginate,
  searchFn,
  customFilter,
  selectionMode,
  value,
  open,
  titleText,
  confirmButtonText,
  onChange,
  onConfirm,
  onOpenChange,
  onRenderItemContent,
  className,
}) => (
  <ClientDataSourceWrapper
    data={data}
    paginate={paginate}
    searchFn={searchFn}
    value={value}
    onChange={onChange}
  >
    <DrawerContainer
      i18n={i18n}
      selectionMode={selectionMode}
      titleText={titleText}
      confirmButtonText={confirmButtonText}
      open={open}
      customFilter={customFilter}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
      onRenderItemContent={onRenderItemContent}
      paginate={paginate}
      className={className}
    >
      <Tree />
    </DrawerContainer>
  </ClientDataSourceWrapper>
);

type ClientEntitySelectorEmbeddedProps = EntitySelectorBaseProps & EntitySelectorClientDataProps & EntitySelectorEmbeddedContainerProps;

export const ClientEntitySelectorEmbedded: React.FC<ClientEntitySelectorEmbeddedProps> = ({
  i18n,
  data,
  paginate,
  searchFn,
  className,
  value,
  onChange,
  children,
  customFilter,
  childrenProps,
}) => (
  <ClientDataSourceWrapper
    data={data}
    paginate={paginate}
    searchFn={searchFn}
    value={value}
    onChange={onChange}
  >
    <EmbeddedContainer
      i18n={i18n}
      className={className}
      customFilter={customFilter}
      childrenProps={childrenProps}
    >
      {children}
    </EmbeddedContainer>
  </ClientDataSourceWrapper>
);
